import {Component, Inject, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {distinctUntilChanged, finalize, Observable, Subject, takeUntil} from 'rxjs';
import {Customer} from '@iw-shop/customer/models/customer';
import {AppState} from '@store/state/app.state';
import {ArticleService} from '@iw-shop/article/services/article.service';
import {ConfigService} from '@iw-shop/config/service/config.service';
import {UserService} from '@iw-shop/user/services/user.service';
import {Store} from '@ngrx/store';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {removeCustomerArticleFavorite, setCustomerArticleFavorites} from '@store/actions/user.action';
import {getSelectedCustomer} from '@store/selectors/user.selector';
import {ApiArticle, ApiArticlesGetRequest} from '@api/articles';
import {faBars} from "@fortawesome/free-solid-svg-icons";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {isPlatformBrowser, ViewportScroller} from "@angular/common";
import {PageEvent} from "@iw-shop/core/components/paginator/paginator.component";

@Component({
    selector: 'iw-shop-customer-favorites',
    standalone: false,
    templateUrl: './customer-favorites.component.html',
    styleUrls: ['./customer-favorites.component.scss']
})
export class CustomerFavoritesComponent implements OnInit {
    @ViewChild('virtualScroll') virtualScroll!: CdkVirtualScrollViewport;
    destroy$: Subject<boolean> = new Subject<boolean>();
    customer$?: Observable<Customer | null | undefined>;

    private readonly isBrowser: boolean;

    //articleRequestParamsDefault: ApiArticleGetRequestParams = {};
    articleRequestParams: ApiArticlesGetRequest['params'] = {};

    customerId?: string;
    favoritesList: string[] = [];

    sort: string = 'nameASC';

    loading: boolean = true;

    articles: ApiArticle[] = [];

    articleItemHeight: number = 600;

    start = 0;
    limit = 40;
    length = this.articles.length;


    constructor(private store: Store<AppState>, @Inject(PLATFORM_ID) platformId: Object, public articlesService: ArticleService, private configService: ConfigService, private userService: UserService, private viewportScroller: ViewportScroller) {


        this.isBrowser = isPlatformBrowser(platformId);
        this.customer$ = this.store.select(getSelectedCustomer);
        this.articlesService.setAvailabilities(this.configService.getAvailabilities());

        if (this.isBrowser) {
            const browserWidth = window.innerWidth;

            if (browserWidth > 1140) {
                this.articleItemHeight = 185;
            } else if (browserWidth > 770) {
                this.articleItemHeight = 268;
            } else if (browserWidth > 420) {
                this.articleItemHeight = 350;
            }


        }
    }


    changePage(event: PageEvent) {

        this.start = event.start;
        this.limit = event.limit;
        window.scrollTo({
            top: 0
        });

    }

    drop(event: CdkDragDrop<string[]>) {
        // Adjust the indices for the full articles array
        const actualPreviousIndex = this.start + event.previousIndex;
        const actualCurrentIndex = this.start + event.currentIndex;

        // Update the full array with reordered items
        moveItemInArray(this.articles, actualPreviousIndex, actualCurrentIndex);

        // Update favorites list
        const favoritesList: string[] = this.articles.map(article => article.articleNumber);

        // Dispatch the updated favorites list
        this.store.dispatch(setCustomerArticleFavorites({
            customerId: this.customerId || '',
            favoritesList
        }));

    }

    setSort(e: any) {
        this.loading = true;
        this.sort = e.target.value;
        if (this.articleRequestParams.articles?.sortByPassedArticleNumbers) {
            delete this.articleRequestParams.articles.sortByPassedArticleNumbers;
        }
        if (this.articleRequestParams.articles?.sort) {
            delete this.articleRequestParams.articles.sort;
        }

        if (this.articleRequestParams.articles) {
            switch (e.target.value) {
                case 'nameASC':
                    this.articleRequestParams.articles.sort = [{field: 'groups.groupName', direction: 1}, {
                        field: 'name',
                        direction: 1
                    }];
                    break;
                case 'nameDESC':
                    this.articleRequestParams.articles.sort = [{field: 'groups.groupName', direction: 1}, {
                        field: 'name',
                        direction: -1
                    }];
                    break;

                case 'group':
                    this.articleRequestParams.articles.sort = [{
                        field: 'groups.groupName',
                        direction: 1
                    }, {field: 'groups.group1Name', direction: 1}];
                    break;

                default:
                    this.articleRequestParams.articles.sortByPassedArticleNumbers = this.favoritesList;
                    break;

            }
        }
        this.loadArticles();
    }

    getArticles() {
        //return this.articles;
        return this.articles.slice(this.start, (this.start + this.limit)); // Returns the first 10 articles

    }

    ngOnInit(): void {
        this.store.select(getSelectedCustomer)
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged((prev, curr) => prev?.customerNumber === curr?.customerNumber)
            )
            .subscribe(
                customer => {
                    this.articles = [];
                    if (customer?.favoritesList) {
                        this.favoritesList = customer.favoritesList;
                        this.customerId = customer.id;
                        this.articleRequestParams = this.configService.getDefaultArticleListRequestParams();
                        this.articleRequestParams.filters = [{
                            field: 'articleNumber',
                            value: "^(" + customer.favoritesList.join('|') + ")$",
                            useRegex: 1
                        }]
                        this.articleRequestParams.articles = {
                            sortByPassedArticleNumbers: customer.favoritesList,
                            limit: 2500,
                            expectedData: {
                                images: 1,
                                notifications: 1,
                                quantity: 1,
                                groups: 1,
                                unitAddition: 1,
                                unit: 1,
                                packageType: 1,
                                unitOfMeasurement: 1,
                                articleNumber: 1,
                                name: 1,
                                availability: 1,
                                inventory: 1,
                                taxRate: 1,
                                bestPrice: 1,
                                bestPrices: 1

                            }
                        };
                        this.setSort({target: {value: this.sort}});
                        this.loadArticles();
                    } else {
                        this.loading = false;
                    }
                }
            )
    }


    loadArticles() {
        this.articlesService.get(this.articleRequestParams)
            .pipe(
                finalize(
                    () => {
                        this.loading = false;
                    }
                )
            )
            .subscribe(
                r => {
                    this.articles = [];
                    r.result?.articles?.data.map((article) => {
                            this.articles.push(article);
                        }
                    );
                }
            );
    }

    removeFavorite(articleNumber: string) {
        this.store.dispatch(removeCustomerArticleFavorite({customerId: this.customerId || '', articleNumber}));
        this.articles = this.articles.filter(article => articleNumber !== article.articleNumber);
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    protected readonly faBars = faBars;
}
